'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import BuyerLoginForm from './BuyerLoginForm';
import BuyerRegisterForm from './BuyerRegisterForm';
import SellerRegisterForm from './SellerRegisterForm';
import SellerLoginForm from './SellerLoginForm';

type Tab = 'login' | 'register';
type Role = 'buyer' | 'seller';

export default function AuthScreen() {
  const [tab, setTab] = useState<Tab>('login');
  const [role, setRole] = useState<Role>('buyer');

  return (
    <div className="min-h-screen flex">
      {/* Left brand panel */}
      <div className="hidden lg:flex lg:w-[480px] xl:w-[540px] flex-shrink-0 bg-primary flex-col justify-between p-10 relative overflow-hidden">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-64 h-64 rounded-full bg-white" />
          <div className="absolute bottom-20 right-10 w-96 h-96 rounded-full bg-white" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-48 h-48 rounded-full bg-white" />
        </div>

        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-12">
            <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center">
              <AppLogo size={28} />
            </div>
            <span className="text-white font-800 text-2xl tracking-tight">LocalMandi</span>
          </div>

          <div className="mb-8">
            <div className="inline-flex items-center gap-1.5 bg-white/20 text-white text-xs font-700 px-3 py-1.5 rounded-full mb-4">
              📍 Mysore&apos;s B2B Wholesale Platform
            </div>
            <h1 className="text-white font-800 text-4xl leading-tight mb-4">
              Mysore&apos;s B2B Wholesale Price Discovery Platform
            </h1>
            <p className="text-white/80 text-lg leading-relaxed">
              Compare prices from verified local wholesalers, distributors, and dealers in Mysore — all in one place.
            </p>
          </div>

          <div className="space-y-4">
            {[
              { icon: '✓', text: 'Compare prices across 50+ verified sellers in Mysore' },
              { icon: '✓', text: 'Real-time price alerts when your products drop in price' },
              { icon: '✓', text: 'Direct order placement with pickup or doorstep delivery' },
              { icon: '✓', text: 'Trusted by 500+ businesses across Mysore city' },
            ].map((item, i) => (
              <div key={`feature-${i + 1}`} className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-white text-sm font-700 flex-shrink-0 mt-0.5">
                  {item.icon}
                </span>
                <p className="text-white/90 text-sm leading-relaxed">{item.text}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10">
          <div className="grid grid-cols-3 gap-4">
            {[
              { value: '500+', label: 'Active Buyers' },
              { value: '120+', label: 'Verified Sellers' },
              { value: '₹2.1Cr', label: 'Monthly GMV' },
            ].map((stat) => (
              <div key={`stat-${stat.label}`} className="text-center">
                <p className="text-white font-800 text-2xl tabular-nums">{stat.value}</p>
                <p className="text-white/70 text-xs mt-0.5">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex flex-col overflow-y-auto bg-background">
        {/* Mobile logo */}
        <div className="flex items-center gap-2 p-6 lg:hidden border-b border-border">
          <AppLogo size={32} />
          <div>
            <span className="font-800 text-xl text-foreground tracking-tight">LocalMandi</span>
            <span className="ml-2 text-xs text-muted-foreground">📍 Mysore</span>
          </div>
        </div>

        <div className="flex-1 flex flex-col justify-center px-6 py-8 sm:px-10 max-w-lg mx-auto w-full">
          {/* Tab switcher */}
          <div className="flex rounded-lg bg-muted p-1 mb-6">
            {(['login', 'register'] as Tab[]).map((t) => (
              <button
                key={`tab-${t}`}
                onClick={() => setTab(t)}
                className={`flex-1 py-2 rounded-md text-sm font-600 transition-all duration-150 ${
                  tab === t
                    ? 'bg-card text-foreground shadow-card'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {t === 'login' ? 'Sign In' : 'Create Account'}
              </button>
            ))}
          </div>

          {/* Role toggle (register only) */}
          {tab === 'register' && (
            <div className="mb-5">
              <p className="text-xs font-600 text-muted-foreground uppercase tracking-wider mb-2">
                I am a
              </p>
              <div className="grid grid-cols-2 gap-3">
                {(['buyer', 'seller'] as Role[]).map((r) => (
                  <button
                    key={`role-${r}`}
                    onClick={() => setRole(r)}
                    className={`flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all duration-150 ${
                      role === r
                        ? 'border-primary bg-primary-light' :'border-border bg-card hover:border-primary/50'
                    }`}
                  >
                    <span className="text-2xl">
                      {r === 'buyer' ? '🛒' : '🏪'}
                    </span>
                    <div className="text-center">
                      <p className={`text-sm font-700 ${role === r ? 'text-primary' : 'text-foreground'}`}>
                        {r === 'buyer' ? 'Bulk Buyer' : 'Seller / Wholesaler'}
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {r === 'buyer' ? 'I want to buy wholesale' : 'I want to sell wholesale'}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Forms */}
          {tab === 'login' && role === 'buyer' && <BuyerLoginForm />}
          {tab === 'login' && role === 'seller' && <SellerLoginForm />}
          {tab === 'register' && role === 'buyer' && <BuyerRegisterForm />}
          {tab === 'register' && role === 'seller' && <SellerRegisterForm />}

          {/* Terms */}
          {tab === 'register' && (
            <p className="text-xs text-muted-foreground text-center mt-4">
              By creating an account, you agree to our{' '}
              <Link href="#" className="text-primary hover:underline">Terms of Service</Link>
              {' '}and{' '}
              <Link href="#" className="text-primary hover:underline">Privacy Policy</Link>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}